<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap mb-2 class="grey lighten-4 pa-2">
      <v-flex xs12 sm4>
        <h2>Settlement & Funding</h2>
        <div v-if="lastUpdated" class="caption grey--text">
          Last updated: {{ formatRelativeTime(lastUpdated) }}
        </div>
      </v-flex>
      <v-flex xs12 sm8 class="text-xs-right">
        <v-switch
          v-model="autoRefresh"
          label="Auto-refresh"
          color="primary"
          hide-details
          class="mt-0 d-inline-flex mr-3"
          style="max-width: 150px;"
        ></v-switch>
        <v-autocomplete
          v-model="selectedMerchantKsuids"
          :items="merchantOptions"
          :search-input.sync="merchantSearchInput"
          :filter="merchantFilter"
          item-text="text"
          item-value="value"
          label="Merchant Filter"
          placeholder="All merchants"
          multiple
          chips
          deletable-chips
          clearable
          hide-details
          small-chips
          :menu-props="{ closeOnContentClick: false }"
          style="max-width: 300px; display: inline-flex;"
          class="mr-3"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index < 2"
              small
              close
              @input="removeMerchantChip(item)"
            >
              {{ item.text }}
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text caption ml-2"
            >
              (+{{ selectedMerchantKsuids.length - 2 }} more)
            </span>
          </template>
          <template v-slot:prepend-item>
            <v-list-tile @click="toggleSelectAllMerchants" v-if="merchantSearchInput === null || merchantSearchInput === ''">
              <v-list-tile-action>
                <v-icon :color="selectedMerchantKsuids.length > 0 ? 'primary' : ''">
                  {{ selectAllMerchantsIcon }}
                </v-icon>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ selectAllMerchantsLabel }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-divider class="mt-2"></v-divider>
          </template>
        </v-autocomplete>
        <v-select
          v-model="dateRange"
          :items="dateRangeOptions"
          label="Date Range"
          style="max-width: 200px; display: inline-flex;"
          class="mr-3"
          hide-details
          @change="applyDateFilter"
        ></v-select>
        <v-btn color="primary" @click="refreshData" :loading="loading">
          <v-icon left>fa-refresh</v-icon>
          Refresh
        </v-btn>
      </v-flex>
    </v-layout>

    <v-dialog v-model="showDatePicker" max-width="500px" persistent>
      <v-card>
        <v-card-title class="pa-2">
          <h3 class="font-weight-regular">Select Custom Date Range</h3>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-layout row wrap>
            <v-flex xs12 sm6>
              <div class="caption grey--text mb-2">Start Date</div>
              <v-date-picker
                v-model="customStartDate"
                :max="customEndDate || maxDate"
                no-title
                scrollable
              ></v-date-picker>
            </v-flex>
            <v-flex xs12 sm6>
              <div class="caption grey--text mb-2">End Date</div>
              <v-date-picker
                v-model="customEndDate"
                :min="customStartDate"
                :max="maxDate"
                no-title
                scrollable
              ></v-date-picker>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions class="pa-2">
          <v-spacer></v-spacer>
          <v-btn flat @click="cancelCustomRange">Cancel</v-btn>
          <v-btn color="primary" @click="applyCustomRange" :disabled="!customStartDate || !customEndDate">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs
      v-model="activeTab"
      slider-color="primary"
      class="funding-tabs elevation-1 mb-3"
      height="48"
    >
      <v-tab key="dashboard" ripple>Performance Overview</v-tab>
      <v-tab key="analytics" ripple>Card Brand Analysis</v-tab>
      <v-tab key="trends" ripple>Historical Trends</v-tab>
      <v-tab key="data" ripple>Batch & Transactions</v-tab>

      <v-tab-item key="dashboard">
        <dashboard-view
          :metrics="apiData.dashboardMetrics"
          :rankings="apiData.clientRankings"
          :loading="loading"
          @rankings-pagination-change="refreshRankings"
        ></dashboard-view>
      </v-tab-item>

      <v-tab-item key="analytics">
        <analytics-view
          :card-volume-by-brand="apiData.cardVolumeByBrand"
          :merchants="fullMerchantList"
          :card-transactions="apiData.cardTransactions"
          :selected-merchant-ksuids="selectedMerchantKsuids"
          :loading="loading"
        ></analytics-view>
      </v-tab-item>

      <v-tab-item key="trends">
        <trends-view
          :merchants="fullMerchantList"
          :start-date="currentStartDate"
          :end-date="currentEndDate"
          :selected-merchant-ksuids="selectedMerchantKsuids"
          :loading="loading"
        ></trends-view>
      </v-tab-item>

      <v-tab-item key="data">
        <data-view
          :funding-batches="apiData.fundingBatches"
          :selected-merchant-ksuids="selectedMerchantKsuids"
          :loading="loading"
          @filter-change="refreshBatches"
        ></data-view>
      </v-tab-item>
    </v-tabs>
  </v-container>
</template>

<script>
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import StoreMixin from '@/store/mixin'
import FundingApi from '@/mixins/fundingApi'
import DashboardView from './funding/DashboardView'
import AnalyticsView from './funding/AnalyticsView'
import TrendsView from './funding/TrendsView'
import DataView from './funding/DataView'

export default {
  mixins: [ApiMixin, AuthMixin, StoreMixin, FundingApi],

  components: {
    DashboardView,
    AnalyticsView,
    TrendsView,
    DataView
  },

  data () {
    return {
      loading: false,
      activeTab: 0,
      dateRange: 'mtd',
      dateRangeOptions: [
        { text: 'Month to Date', value: 'mtd' },
        { text: 'Last 7 Days', value: '7' },
        { text: 'Last 30 Days', value: '30' },
        { text: 'Last 90 Days', value: '90' },
        { text: 'Year to Date', value: 'ytd' },
        { text: 'Custom Range', value: 'custom' }
      ],
      showDatePicker: false,
      customStartDate: null,
      customEndDate: null,
      previousDateRange: 'mtd',
      currentStartDate: new Date(),
      currentEndDate: new Date(),
      lastUpdated: null,
      autoRefresh: false,
      refreshInterval: null,
      merchantFilterDebounce: null,
      selectedMerchantKsuids: [],
      merchantSearchInput: null,
      fullMerchantList: [],
      apiData: {
        dashboardMetrics: null,
        clientRankings: [],
        cardVolumeByBrand: {},
        volumeOverTime: null,
        fundingBatches: [],
        merchants: [],
        cardTransactions: []
      }
    }
  },

  computed: {
    maxDate () {
      const today = new Date()
      return today.toISOString().substr(0, 10)
    },

    merchantOptions () {
      if (!this.fullMerchantList || !Array.isArray(this.fullMerchantList)) {
        return []
      }
      const options = this.fullMerchantList.map(m => {
        const name = m.merchantDbaName || 'Unknown Merchant'
        return {
          text: m.hasTransactions === false ? `${name} (no data)` : name,
          value: m.entityKsuid
        }
      })
      console.log('[Funding] merchantOptions count:', options.length)
      return options
    },

    selectAllMerchantsIcon () {
      const merchantCount = this.fullMerchantList ? this.fullMerchantList.length : 0
      if (this.selectedMerchantKsuids.length === merchantCount && merchantCount > 0) {
        return 'fa-check-square'
      }
      if (this.selectedMerchantKsuids.length > 0) {
        return 'fa-minus-square'
      }
      return 'fa-square-o'
    },

    selectAllMerchantsLabel () {
      const merchantCount = this.fullMerchantList ? this.fullMerchantList.length : 0
      if (this.selectedMerchantKsuids.length === merchantCount && merchantCount > 0) {
        return 'Deselect All'
      }
      return 'Select All'
    }
  },

  async mounted () {
    const hash = this.$route.hash.replace('#', '')
    const tabMap = { dashboard: 0, analytics: 1, trends: 2, data: 3 }
    if (tabMap[hash] !== undefined) {
      this.activeTab = tabMap[hash]
    }

    const prefs = this.$store.state.fundingPreferences
    if (prefs.dateRange) {
      this.dateRange = prefs.dateRange
      if (prefs.dateRange === 'custom' && prefs.customStartDate && prefs.customEndDate) {
        this.customStartDate = prefs.customStartDate
        this.customEndDate = prefs.customEndDate
      }
    }

    if (prefs.selectedMerchantKsuids && prefs.selectedMerchantKsuids.length > 0) {
      this.selectedMerchantKsuids = [...prefs.selectedMerchantKsuids]
    }

    this.loadMerchants()
    this.loadData()
  },

  watch: {
    activeTab (newTab) {
      const names = ['dashboard', 'analytics', 'trends', 'data']
      this.$router.replace({ hash: `#${names[newTab]}` })
    },

    dateRange (newValue) {
      this.$store.commit('setFundingDateRange', newValue)
    },

    selectedMerchantKsuids (newValue) {
      if (this.merchantFilterDebounce) {
        clearTimeout(this.merchantFilterDebounce)
      }

      this.merchantFilterDebounce = setTimeout(() => {
        this.$store.commit('setFundingMerchantFilter', newValue)

        if (this.fullMerchantList.length > 0) {
          this.refreshFilteredData()
        }
      }, 500)
    },

    autoRefresh (enabled) {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }

      if (enabled) {
        this.refreshInterval = setInterval(() => {
          if (document.visibilityState === 'visible') {
            this.loadData()
          }
        }, 300000)
      }
    }
  },

  beforeDestroy () {
    if (this.merchantFilterDebounce) {
      clearTimeout(this.merchantFilterDebounce)
      this.$store.commit('setFundingMerchantFilter', this.selectedMerchantKsuids)
    }

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },

  methods: {
    async loadMerchants () {
      try {
        console.log('[Funding] Loading merchant list...')
        const response = await this.getMerchants({ limit: 1000 })

        if (response.data && response.data.rows) {
          this.fullMerchantList = response.data.rows.map(m => ({
            entityKsuid: m.entity_ksuid,
            merchantDbaName: m.dba_name || m.merchant_name,
            isActive: m.is_active,
            hasTransactions: m.has_transactions_in_period
          }))
          console.log('[Funding] Loaded merchant list:', this.fullMerchantList.length)
        }
      } catch (error) {
        console.error('[Funding] Failed to load merchants:', error)
        this.$store.commit('warning', 'Failed to load merchant list')
      }
    },

    merchantFilter (item, queryText, itemText) {
      if (!queryText) return true

      const searchText = queryText.toLowerCase()
      const text = itemText.toLowerCase()

      return text.indexOf(searchText) > -1
    },

    removeMerchantChip (item) {
      const index = this.selectedMerchantKsuids.indexOf(item.value)
      if (index >= 0) {
        this.selectedMerchantKsuids.splice(index, 1)
      }
    },

    toggleSelectAllMerchants () {
      if (!this.fullMerchantList || this.fullMerchantList.length === 0) {
        return
      }
      const merchantCount = this.fullMerchantList.length
      if (this.selectedMerchantKsuids.length === merchantCount) {
        this.selectedMerchantKsuids = []
      } else {
        this.selectedMerchantKsuids = this.fullMerchantList.map(m => m.entityKsuid)
      }
    },

    calculateDateRange () {
      const now = new Date()
      let startDate
      let endDate = now

      switch (this.dateRange) {
        case 'mtd':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1)
          break
        case 'ytd':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        case '7':
          startDate = new Date(now)
          startDate.setDate(startDate.getDate() - 7)
          break
        case '30':
          startDate = new Date(now)
          startDate.setDate(startDate.getDate() - 30)
          break
        case '90':
          startDate = new Date(now)
          startDate.setDate(startDate.getDate() - 90)
          break
        case 'custom':
          if (this.customStartDate && this.customEndDate) {
            startDate = new Date(this.customStartDate)
            endDate = new Date(this.customEndDate)
          } else {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1)
            endDate = now
          }
          break
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1)
      }

      return { startDate, endDate }
    },

    async loadData () {
      this.loading = true
      try {
        const { startDate, endDate } = this.calculateDateRange()
        this.currentStartDate = startDate
        this.currentEndDate = endDate

        const merchantFilter = this.selectedMerchantKsuids.length > 0 ? this.selectedMerchantKsuids : null

        const promises = [
          this.getDashboardMetrics(startDate, endDate).catch(err => ({ error: err })),
          this.getClientRankings(startDate, endDate, 10, 0).catch(err => ({ error: err })),
          this.getCardTypeBreakdown(startDate, endDate, 'day').catch(err => ({ error: err })),
          this.getFundingBatches({ startDate, endDate, merchantIds: merchantFilter, limit: 25, offset: 0 }).catch(err => ({ error: err })),
          this.getTrendsData(startDate, endDate, merchantFilter).catch(err => ({ error: err }))
        ]

        const results = await Promise.all(promises)

        let failedCount = 0

        if (results[0].error) {
          console.error('Failed to load metrics:', results[0].error)
          this.apiData.dashboardMetrics = null
          failedCount++
        } else {
          this.apiData.dashboardMetrics = results[0].data.data
        }

        if (results[1].error) {
          console.error('Failed to load rankings:', results[1].error)
          this.apiData.clientRankings = []
          failedCount++
        } else {
          this.apiData.clientRankings = results[1].data
        }

        if (results[2].error) {
          console.error('Failed to load card breakdown:', results[2].error)
          this.apiData.cardVolumeByBrand = {}
          failedCount++
        } else {
          this.apiData.cardVolumeByBrand = results[2].data
        }

        if (results[3].error) {
          console.error('Failed to load batches:', results[3].error)
          this.apiData.fundingBatches = []
          failedCount++
        } else {
          this.apiData.fundingBatches = results[3].data
        }

        if (results[4].error) {
          console.error('Failed to load trends:', results[4].error)
          this.apiData.merchants = []
          failedCount++
        } else {
          this.apiData.merchants = results[4].data.data.merchants
        }

        if (failedCount > 0) {
          this.$store.commit('warning', `Some data failed to load (${failedCount}/${results.length} endpoints). Showing partial data.`)
        }

        this.lastUpdated = new Date()
      } catch (error) {
        console.error('Error loading data:', error)

        let errorMessage = 'Failed to load funding data'
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'Access denied - please contact your administrator'
          } else if (error.response.status === 404) {
            errorMessage = 'Funding data not found'
          } else if (error.response.status >= 500) {
            errorMessage = 'Server error - please try again later'
          }
        } else if (error.message === 'Network Error') {
          errorMessage = 'Network error - please check your connection'
        }

        this.$store.commit('error', errorMessage)
      } finally {
        this.loading = false
      }
    },

    async refreshFilteredData () {
      this.loading = true
      try {
        const { startDate, endDate } = this.calculateDateRange()
        const merchantFilter = this.selectedMerchantKsuids.length > 0 ? this.selectedMerchantKsuids : null

        const [batchesResponse, trendsResponse] = await Promise.all([
          this.getFundingBatches({ startDate, endDate, merchantIds: merchantFilter, limit: 25, offset: 0 }),
          this.getTrendsData(startDate, endDate, merchantFilter)
        ])

        this.apiData.fundingBatches = batchesResponse.data
        this.apiData.merchants = trendsResponse.data.data.merchants
        this.lastUpdated = new Date()
      } catch (error) {
        console.error('Error refreshing filtered data:', error)

        let errorMessage = 'Failed to refresh filtered data'
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'Access denied - please contact your administrator'
          } else if (error.response.status === 404) {
            errorMessage = 'Funding data not found'
          } else if (error.response.status >= 500) {
            errorMessage = 'Server error - please try again later'
          }
        } else if (error.message === 'Network Error') {
          errorMessage = 'Network error - please check your connection'
        }

        this.$store.commit('error', errorMessage)
      } finally {
        this.loading = false
      }
    },

    refreshData () {
      this.loadData()
      this.$store.commit('success', 'Data refreshed successfully')
    },

    async refreshBatches (filterParams = {}) {
      this.loading = true
      try {
        const { startDate, endDate } = this.calculateDateRange()
        const merchantFilter = this.selectedMerchantKsuids.length > 0 ? this.selectedMerchantKsuids : null

        const batchesResponse = await this.getFundingBatches({
          startDate,
          endDate,
          search: filterParams.search || null,
          status: filterParams.status || null,
          type: filterParams.type || null,
          merchantIds: merchantFilter,
          limit: filterParams.limit || 25,
          offset: filterParams.offset || 0,
          sortBy: filterParams.sortBy || 'date',
          sortOrder: filterParams.sortOrder || 'desc'
        })

        this.apiData.fundingBatches = batchesResponse.data
      } catch (error) {
        console.error('Error refreshing batches:', error)

        let errorMessage = 'Failed to refresh batch data'
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'Access denied - please contact your administrator'
          } else if (error.response.status === 404) {
            errorMessage = 'Batch data not found'
          } else if (error.response.status >= 500) {
            errorMessage = 'Server error - please try again later'
          }
        } else if (error.message === 'Network Error') {
          errorMessage = 'Network error - please check your connection'
        }

        this.$store.commit('error', errorMessage)
      } finally {
        this.loading = false
      }
    },

    async refreshRankings (paginationParams = {}) {
      this.loading = true
      try {
        const { startDate, endDate } = this.calculateDateRange()

        const rankingsResponse = await this.getClientRankings(
          startDate,
          endDate,
          paginationParams.limit || 10,
          paginationParams.offset || 0,
          paginationParams.sortBy || null,
          paginationParams.sortOrder || 'desc'
        )

        this.apiData.clientRankings = rankingsResponse.data
      } catch (error) {
        console.error('Error refreshing rankings:', error)

        let errorMessage = 'Failed to refresh rankings data'
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'Access denied - please contact your administrator'
          } else if (error.response.status === 404) {
            errorMessage = 'Rankings data not found'
          } else if (error.response.status >= 500) {
            errorMessage = 'Server error - please try again later'
          }
        } else if (error.message === 'Network Error') {
          errorMessage = 'Network error - please check your connection'
        }

        this.$store.commit('error', errorMessage)
      } finally {
        this.loading = false
      }
    },

    applyDateFilter () {
      if (this.dateRange === 'custom') {
        this.previousDateRange = this.dateRange
        this.showDatePicker = true
      } else {
        this.loadData()
      }
    },

    applyCustomRange () {
      this.showDatePicker = false
      this.$store.commit('setFundingCustomDates', {
        startDate: this.customStartDate,
        endDate: this.customEndDate
      })
      this.loadData()
      this.$store.commit('success', `Custom range applied: ${this.formatDate(this.customStartDate)} to ${this.formatDate(this.customEndDate)}`)
    },

    cancelCustomRange () {
      this.showDatePicker = false
      if (!this.customStartDate || !this.customEndDate) {
        this.dateRange = this.previousDateRange
      }
    },

    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },

    formatRelativeTime (date) {
      if (!date) return ''
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)

      if (diffMins < 1) return 'just now'
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`

      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`

      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    }
  }
}
</script>

<style scoped>
.funding-tabs {
  background-color: #ffffff !important;
}

.funding-tabs .v-tabs__bar {
  background-color: #ffffff !important;
}

.funding-tabs .v-tabs__div {
  color: rgba(0, 0, 0, 0.54);
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
}

.funding-tabs .v-tabs__item--active {
  color: var(--v-primary-base);
  font-weight: 600;
}

.funding-tabs .v-tabs__slider {
  height: 3px;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/paynetworx/Funding.vue